import { useCallback, useEffect, useRef, useState } from 'react';

import { store } from 'store';
import { setDrawerOpen } from 'store/drawerSlice';
import { clearFeatureInfoByggnader } from 'store/featureInfoByggnaderSlice';
import { clearFeatureInfoInventeringar } from 'store/featureInfoInventeringarSlice';
import { setActiveInteraction, setFocusOnExtent, setMapMode, setVisibleMapExtent } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';
import { useMapInteraction } from 'hooks/useMapInteraction';

import FeatureInfoPopup from 'components/featureInfoPopup/FeatureInfoPopup';
import LayerDrawer from 'components/layerDrawer/LayerDrawer';
import LmByggnadPopup from 'components/lmByggnadPopup/LmByggnadPopup';
import InfoBox from 'components/map/infoBox/InfoBox';
import InteractionSelect from 'components/map/interactionSelect/InteractionSelect';

import { DrawerSize } from 'models';

import { InteractionTypeEnum, Layer, MapModeEnum } from 'enums';

import { Feature, MapBrowserEvent } from 'ol';
import Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import { ScaleLine } from 'ol/control';
import { Coordinate } from 'ol/coordinate';
import { inAndOut } from 'ol/easing';
import { EventsKey } from 'ol/events';
import { Extent, boundingExtent, getBottomRight, getCenter, getTopLeft, isEmpty } from 'ol/extent';
import { Polygon } from 'ol/geom';
import { Interaction, defaults } from 'ol/interaction';

import {
  InteractionType,
  drawHoleInteraction,
  drawInteraction,
  modifyDeleteInteraction,
  modifyInteraction,
  selectInteraction,
  transformInteraction,
  undoInteraction
} from './Interactions';
import {
  activeSymbolerLayer,
  activeYtaLayer,
  addDrawVectorEventListener,
  addMapUrlToLayers,
  clearFeaturesInTemplateSources,
  clearSelectedLmByggnadLayers,
  drawDeleteVectorLayer,
  drawDeleteVectorSource,
  drawVectorLayer,
  drawVectorSource,
  featureInfoPopupOverlay,
  ortofoto,
  popupOverlay,
  publiceradeAktiviteterSymbolerWMTS,
  publiceradeAktiviteterYtorWMTS,
  publiceradeBebyggelseObjektSymbolerWMS,
  publiceradeBebyggelseObjektYtorWMS,
  publiceradeBebyggelseObjektYtorWMTS,
  publiceradeBebyggelseobjektSymbolerWMTS,
  replaceFeaturesInSelectedLmByggnadSource,
  replaceFeaturesInTemplatePolygonSource,
  selectedLmByggnadsPolygonLayer,
  setApplicationLayersVisible,
  setBackgroundLayersVisible,
  setForegroundLayersVisible,
  sjokort,
  templatePointLayer,
  templatePolygonLayer,
  terranglutning,
  terrangskuggning,
  topoweb,
  wmsFastighetGranser,
  wmsLandskap,
  wmsSocken
} from './Layers';
import './Map.scss';
import { handleClick } from './MapClick';
import { controls, view } from './MapConfig';
import { handlePointerMove } from './MapPointerMove';
import { drawDeleteStyle, drawPolygonPointsStyle, drawPolygonStyle } from './Styles';

import { useSearchLmByggnadMutation } from 'api/endpoints/LMByggnadSlice';
import { isMobile } from 'utilities/isMobile';

const animateCenter = (extentToAnimate: Extent, visibleMapExtent: Extent) => {
  const newCenter = view.getCenter();
  const resolution = view.getResolution();
  const extentToAnimateCenter = getCenter(extentToAnimate);
  const mapOuterTopLeft = getTopLeft(visibleMapExtent);
  const mapOuterBottomRight = getBottomRight(visibleMapExtent);
  const pixelOffset = 40 * resolution!;

  if (extentToAnimateCenter[0] < mapOuterTopLeft[0]) {
    // Om den är väster om visibleMapExtent, flytta newCenter så att extentToAnimateCenter hamnar på vänsterkanten
    newCenter![0] += extentToAnimateCenter[0] - mapOuterTopLeft[0] - pixelOffset;
  } else if (extentToAnimateCenter[0] > mapOuterBottomRight[0]) {
    // Om den är öster om visibleMapExtent, flytta newCenter så att extentToAnimateCenter hamnar på högerkanten
    newCenter![0] += extentToAnimateCenter[0] - mapOuterBottomRight[0] + pixelOffset;
  }

  if (extentToAnimateCenter[1] > mapOuterTopLeft[1]) {
    // Om den är norr om visibleMapExtent, flytta newCenter så att extentToAnimateCenter hamnar på överkanten
    newCenter![1] += extentToAnimateCenter[1] - mapOuterTopLeft[1] + pixelOffset;
  } else if (extentToAnimateCenter[1] < mapOuterBottomRight[1]) {
    // Om den är söder om visibleMapExtent, flytta newCenter så att extentToAnimateCenter hamnar på underkanten
    newCenter![1] += extentToAnimateCenter[1] - mapOuterBottomRight[1] - pixelOffset;
  }

  view.animate({
    center: newCenter,
    duration: 200
  });
};

export const getDrawerPixelOffset = (drawerSize: DrawerSize) => {
  let pixelOffset = 0;
  if (drawerSize === 'xsmall') {
    pixelOffset = -424; // 26.5rem
  } else if (drawerSize === 'small') {
    pixelOffset = -560; // 35rem
  } else if (drawerSize === 'medium') {
    pixelOffset = -672; // 42rem
  } else if (drawerSize === 'large') {
    pixelOffset = -1040; // 65rem
  }

  return pixelOffset;
};

export const calculateVisibleMapExtent = (map: Map, leftSidePixelPadding: number) => {
  const viewExtent = map.getView().calculateExtent();

  if (viewExtent.length) {
    const co: Coordinate = [viewExtent[0], viewExtent[1]];
    const leftBottomPixel = map.getPixelFromCoordinate(co);
    const newBottomLeft = map.getCoordinateFromPixel([leftBottomPixel[0] - leftSidePixelPadding, leftBottomPixel[1]]!);

    return boundingExtent([
      [newBottomLeft[0], newBottomLeft[1]],
      [viewExtent[2], viewExtent[3]]
    ]);
  }
};

const MapComponent = () => {
  const mapElement = useRef<any>();
  const map = useRef<Map | undefined>();
  const clickEventRef = useRef<EventsKey>();
  const pointerMoveEventRef = useRef<EventsKey>();
  const animateExtentRef = useRef<Extent>();

  const activeInteractions = useAppSelector((state: RootState) => state.map.activeInteractions);
  const configurationData = useAppSelector((state: RootState) => state.configuration.data);
  const drawer = useAppSelector((state: RootState) => state.drawer);
  const focusOnExtent = useAppSelector((state: RootState) => state.map.focusOnExtent);
  const mapMode = useAppSelector((state: RootState) => state.map.mapMode);

  const [, results] = useSearchLmByggnadMutation({ fixedCacheKey: 'lm-byggnad' });
  const { data: lmByggnader, isSuccess: lmByggnaderIsSuccess } = results;

  const visibleBackgroundLayers = useAppSelector((state: RootState) => state.map.visibleBackgroundLayers);
  const visibleForegroundLayers = useAppSelector((state: RootState) => state.map.visibleForegroundLayers);
  const visibleMapExtent = useAppSelector((state: RootState) => state.map.visibleMapExtent);

  const cqlState = useAppSelector((state: RootState) => state.map.cqlBebyggelseLayerWMS);
  const [hoveredCoordinates, setHoveredCoordinates] = useState<Array<number>>([0, 0]);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { interactionOptions } = useMapInteraction();

  const setMapSingleClickActive = useCallback(() => {
    if (!map.current || clickEventRef.current) return;

    clickEventRef.current = map.current.on('singleclick', (event: MapBrowserEvent<MouseEvent>) =>
      handleClick(event, map.current)
    );
  }, []);

  const setMapSingleClickInactive = useCallback(() => {
    if (!clickEventRef.current) return;

    unByKey(clickEventRef.current);
    clickEventRef.current = undefined;
  }, [clickEventRef]);

  const setMapPointerMoveActive = useCallback(() => {
    if (!map.current || pointerMoveEventRef.current) return;

    pointerMoveEventRef.current = map.current.on('pointermove', (event: MapBrowserEvent<MouseEvent>) => {
      setHoveredCoordinates(event.coordinate);
      return handlePointerMove(event, map.current);
    });
  }, []);

  const setMapPointerMoveInActive = useCallback(() => {
    if (!pointerMoveEventRef.current) return;

    unByKey(pointerMoveEventRef.current);
    pointerMoveEventRef.current = undefined;
  }, [pointerMoveEventRef]);

  const getExtendedInteractions = useCallback(() => {
    const interactions = map.current?.getInteractions().getArray();
    const interactionTypes = Object.keys(InteractionTypeEnum) as InteractionType[];

    return (
      interactions?.filter((interaction: Interaction) =>
        interactionTypes.some((type: InteractionType) => type === interaction.getProperties().type)
      ) || []
    );
  }, []);

  const enableMapModeOnlyActiveLayers = useCallback(() => {
    setApplicationLayersVisible([Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA], map.current);
  }, []);

  const enableMapModeUtkastPresentation = useCallback(() => {
    setApplicationLayersVisible(
      [Layer.SELECTED_LM_BYGGNADSPOLYGON, Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA],
      map.current
    );
  }, []);

  const enableMapModeUtkastRegistration = useCallback(() => {
    setApplicationLayersVisible(
      [Layer.SELECTED_LM_BYGGNADSPOLYGON, Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA],
      map.current
    );
  }, []);

  const enableMapModeInventeringPresentation = useCallback(() => {
    setApplicationLayersVisible(
      [Layer.SELECTED_LM_BYGGNADSPOLYGON, Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA],
      map.current
    );
  }, []);

  const enableMapModeInventeringSearch = useCallback(() => {
    featureInfoPopupOverlay.setPosition(undefined);
    popupOverlay.setPosition(undefined);
    setApplicationLayersVisible([Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA], map.current);
  }, []);

  const enableMapModeInventeringRegistration = useCallback(() => {
    setApplicationLayersVisible(
      [Layer.SELECTED_LM_BYGGNADSPOLYGON, Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA],
      map.current
    );
  }, []);

  const enableMapModeAddUtkastToInventering = useCallback(() => {
    setApplicationLayersVisible(
      [
        Layer.SELECTED_LM_BYGGNADSPOLYGON,
        Layer.TEMPLATE_POINT,
        Layer.TEMPLATE_POLYGON,
        Layer.ACTIVE_SYMBOLER,
        Layer.ACTIVE_YTA
      ],
      map.current
    );
  }, []);

  const enableMapModeSammansatt = useCallback(() => {
    setApplicationLayersVisible(
      [Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS, Layer.ACTIVE_SYMBOLER, Layer.ACTIVE_YTA],
      map.current
    );
  }, []);

  const enableMapModeEditGeometry = useCallback(() => {
    store.dispatch(clearFeatureInfoByggnader(undefined));
    store.dispatch(clearFeatureInfoInventeringar(undefined));
    store.dispatch(setDrawerOpen('closed'));
    store.dispatch(setActiveInteraction([InteractionTypeEnum.DRAW, InteractionTypeEnum.MODIFY]));
    setApplicationLayersVisible([Layer.DRAW, Layer.DRAW_DELETE], map.current);
  }, []);

  const onMapInit = useCallback(() => {
    if (!map.current) return;

    map.current.updateSize();
    setInitialized(true);
    map.current.un('postrender', onMapInit);
  }, []);

  const onMoveEnd = useCallback(() => {
    if (!map.current) {
      return;
    }

    const visibleMapExtent = calculateVisibleMapExtent(map.current, getDrawerPixelOffset(drawer.drawerSize));
    if (visibleMapExtent) {
      store.dispatch(setVisibleMapExtent(visibleMapExtent));
    }
  }, [drawer.drawerSize]);

  useEffect(() => {
    if (map.current) return;

    map.current = new Map({
      target: mapElement.current,
      layers: [
        topoweb,
        ortofoto,
        sjokort,
        terranglutning,
        terrangskuggning,
        wmsSocken,
        wmsLandskap,
        wmsFastighetGranser,
        publiceradeAktiviteterYtorWMTS,
        publiceradeBebyggelseObjektYtorWMTS,
        activeYtaLayer,
        publiceradeAktiviteterSymbolerWMTS,
        publiceradeBebyggelseobjektSymbolerWMTS,
        selectedLmByggnadsPolygonLayer,
        activeSymbolerLayer,
        templatePolygonLayer,
        templatePointLayer,
        drawVectorLayer,
        drawDeleteVectorLayer
      ],
      interactions: defaults().extend([
        modifyInteraction,
        drawInteraction,
        drawHoleInteraction,
        transformInteraction,
        modifyDeleteInteraction,
        selectInteraction,
        undoInteraction
      ]),
      overlays: [popupOverlay, featureInfoPopupOverlay],
      controls,
      view
    });
    map.current.addControl(
      new ScaleLine({
        className: 'ol-scale-line',
        target: document.getElementById('scale-line') as HTMLElement
      })
    );

    addDrawVectorEventListener();
  }, []);

  useEffect(() => {
    const zoomControlElement = document.querySelector('.ol-zoom') as HTMLDivElement | null;

    if (zoomControlElement) {
      if (isMobile() && drawer.drawerVisibility === 'minimized') {
        zoomControlElement.style.marginBottom = '6.5rem';
      } else {
        setTimeout(() => (zoomControlElement.style.marginBottom = '1.25rem'), 250);
      }
    }
  }, [drawer.drawerVisibility]);

  useEffect(() => {
    if (!map.current) return;

    map.current.on('postrender', onMapInit);
    map.current.on('moveend', onMoveEnd);
  }, [onMapInit, onMoveEnd]);

  useEffect(() => {
    if (initialized && map.current) {
      const visibleMapExtent = calculateVisibleMapExtent(map.current, getDrawerPixelOffset(drawer.drawerSize));
      if (visibleMapExtent) {
        store.dispatch(setVisibleMapExtent(visibleMapExtent));
      }
    }
  }, [initialized, drawer.drawerSize]);

  useEffect(() => {
    const interactions = getExtendedInteractions();
    interactions.forEach((interaction: Interaction) => interaction.setActive(false));

    if (mapMode !== MapModeEnum.EDIT_GEOMETRY) {
      return;
    }

    if (interactions && interactions.length) {
      if (map.current) {
        setMapSingleClickInactive();

        const style = map.current.getViewport().style;
        if (activeInteractions.includes(InteractionTypeEnum.DRAW)) {
          setMapPointerMoveActive();
          drawVectorLayer.setStyle([drawPolygonStyle, drawPolygonPointsStyle]);
        } else if (activeInteractions.includes(InteractionTypeEnum.DRAW_HOLE)) {
          drawVectorLayer.setStyle([drawPolygonStyle]);
          style.cursor = 'crosshair';
        } else if (activeInteractions.includes(InteractionTypeEnum.TRANSFORM)) {
          style.cursor = 'move';
          drawVectorLayer.setStyle([drawPolygonStyle]);
        } else if (activeInteractions.includes(InteractionTypeEnum.MODIFY_DELETE)) {
          style.cursor = '';
          drawVectorLayer.setStyle(drawDeleteStyle);
          drawDeleteVectorSource.clear();
          drawVectorSource.getFeatures().forEach((feature) => {
            const removeFeature = new Feature({
              geometry: (feature.getGeometry()! as Polygon).getInteriorPoint()
            });
            removeFeature.set('featureToRemove', feature);
            drawDeleteVectorSource.addFeature(removeFeature);
          });
          setMapPointerMoveActive();
          setMapSingleClickActive();
        } else {
          style.cursor = '';
          drawVectorLayer.setStyle(drawPolygonStyle);
        }
      }

      activeInteractions.forEach((interaction: InteractionType) => {
        interactions.find((int: Interaction) => int.getProperties().type === interaction)?.setActive(true);
      });
    }
  }, [
    activeInteractions,
    getExtendedInteractions,
    setMapPointerMoveActive,
    setMapSingleClickActive,
    setMapSingleClickInactive,
    mapMode
  ]);

  useEffect(() => {
    const sourceSymbolerWMTS = publiceradeBebyggelseobjektSymbolerWMTS.getSource();
    const dimensionsSymbolerWMTS = sourceSymbolerWMTS?.getDimensions();

    const sourceYtaWMTS = publiceradeBebyggelseObjektYtorWMTS.getSource();
    const dimensionsYtaWMTS = sourceYtaWMTS?.getDimensions();

    const sourceSymbolerWMS = publiceradeBebyggelseObjektSymbolerWMS.getSource();
    const paramsSymbolerWMS = sourceSymbolerWMS?.getParams();

    const sourceYtaWMS = publiceradeBebyggelseObjektYtorWMS.getSource();
    const paramsYtaWMS = sourceYtaWMS?.getParams();

    if (cqlState) {
      dimensionsSymbolerWMTS.CQL_FILTER = cqlState;
      dimensionsYtaWMTS.CQL_FILTER = cqlState;
      paramsSymbolerWMS.CQL_FILTER = cqlState;
      paramsYtaWMS.CQL_FILTER = cqlState;
    } else {
      delete dimensionsSymbolerWMTS.CQL_FILTER;
      delete dimensionsYtaWMTS.CQL_FILTER;
      delete paramsSymbolerWMS.CQL_FILTER;
      delete paramsYtaWMS.CQL_FILTER;
    }

    sourceSymbolerWMTS?.updateDimensions(dimensionsSymbolerWMTS);
    sourceYtaWMTS?.updateDimensions(dimensionsYtaWMTS);
    sourceSymbolerWMS?.updateParams(paramsSymbolerWMS);
    sourceYtaWMS?.updateParams(paramsYtaWMS);
  }, [cqlState]);

  useEffect(() => {
    setMapSingleClickActive();
    setMapPointerMoveActive();

    /** TODO! Denna behöver bort, alternativ reset:a enbart BIP-api.
     * LM-prylarna låg i samma api, vilket gjorde att vid reset försvann all LM-data, vilket
     * i sin tur triggade om anropet och hämtade igen.
     * Detta måste testas om det tas bort helt så att inte kartan eller annat visar massa skrot.
     */
    // store.dispatch(LMByggnadSlice.util?.resetApiState());
    switch (mapMode) {
      case MapModeEnum.INVENTERING_REGISTRATION:
        enableMapModeInventeringRegistration();
        break;
      case MapModeEnum.INVENTERING_PRESENTATION:
        enableMapModeInventeringPresentation();
        break;
      case MapModeEnum.ACTIVE_LAYERS_ONLY:
        enableMapModeOnlyActiveLayers();
        break;
      case MapModeEnum.SEARCH:
        enableMapModeInventeringSearch();
        break;
      case MapModeEnum.UTKAST_REGISTRATION:
        enableMapModeUtkastRegistration();
        break;
      case MapModeEnum.UTKAST_PRESENTATION:
        enableMapModeUtkastPresentation();
        break;
      case MapModeEnum.ADD_UTKAST_TO_INVENTERING:
      case MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING:
        enableMapModeAddUtkastToInventering();
        break;
      case MapModeEnum.ADD_UTKAST_TO_SAMMANSATT:
      case MapModeEnum.SAMMANSATT_UTKAST_PRESENTATION:
        enableMapModeSammansatt();
        break;
      case MapModeEnum.EDIT_GEOMETRY:
        setMapSingleClickInactive();
        setMapPointerMoveInActive();
        enableMapModeEditGeometry();
        break;
    }
  }, [
    mapMode,
    setMapSingleClickInactive,
    setMapPointerMoveInActive,
    enableMapModeInventeringRegistration,
    enableMapModeInventeringPresentation,
    enableMapModeInventeringSearch,
    enableMapModeUtkastRegistration,
    enableMapModeUtkastPresentation,
    enableMapModeAddUtkastToInventering,
    enableMapModeSammansatt,
    enableMapModeEditGeometry,
    setMapSingleClickActive,
    setMapPointerMoveActive,
    enableMapModeOnlyActiveLayers
  ]);

  useEffect(() => {
    if (configurationData?.mapUrl) {
      addMapUrlToLayers(configurationData.mapUrl);
    }
  }, [configurationData?.mapUrl]);

  useEffect(() => {
    if (!shouldAnimate || !visibleMapExtent || !animateExtentRef.current) return;

    animateCenter(animateExtentRef.current, visibleMapExtent);
    setShouldAnimate(false);
  }, [shouldAnimate, visibleMapExtent]);

  useEffect(() => {
    if (!focusOnExtent || (focusOnExtent && isEmpty(focusOnExtent.extent))) return;
    if (map.current?.getView().getAnimating()) return;

    if (focusOnExtent.type === 'animate') {
      animateExtentRef.current = focusOnExtent.extent;
      setShouldAnimate(true);
    } else {
      const drawerOffset = isMobile() ? 40 : Math.abs(getDrawerPixelOffset(drawer.drawerSize) - 40);

      view.fit(focusOnExtent.extent, {
        maxZoom: focusOnExtent.zoomLevel,
        padding: [40, 40, 40, drawerOffset],
        duration: focusOnExtent.duration,
        easing: inAndOut
      });
    }

    store.dispatch(setFocusOnExtent(undefined));
  }, [drawer.drawerSize, focusOnExtent]);

  useEffect(() => {
    setBackgroundLayersVisible(visibleBackgroundLayers);
  }, [visibleBackgroundLayers]);

  useEffect(() => {
    setForegroundLayersVisible(visibleForegroundLayers);
  }, [visibleForegroundLayers]);

  useEffect(() => {
    if (lmByggnader?.requestedAtCoordinate) {
      replaceFeaturesInTemplatePolygonSource(lmByggnader.requestedAtCoordinate);
    }
  }, [lmByggnader?.requestedAtCoordinate]);

  useEffect(() => {
    if (lmByggnaderIsSuccess) {
      if (lmByggnader && lmByggnader.byggnadIndex.length > 0) {
        replaceFeaturesInSelectedLmByggnadSource(lmByggnader.byggnadIndex);
      } else {
        clearSelectedLmByggnadLayers();
      }

      activeSymbolerLayer.changed();
    }
  }, [lmByggnader, lmByggnaderIsSuccess]);

  const getCopyrightText = (backgroundLayer: Array<string>): string => {
    const layer = backgroundLayer[0];
    switch (layer) {
      case 'TOPOWEB':
      case 'ORTOFOTO':
      case 'SKUGGNING':
      case 'LUTNING':
        return '© Lantmäteriet. SWEREF 99 TM';
      case 'SJOKORT':
        return '© Sjöfartsverket tillstånd nr 22-00635';
      default:
        return '© Lantmäteriet. SWEREF 99 TM';
    }
  };

  return (
    <>
      <LmByggnadPopup />
      <FeatureInfoPopup />
      <LayerDrawer />
      <div ref={mapElement} className="tw-relative tw-h-full tw-w-full tw-overflow-hidden" />
      {(mapMode === MapModeEnum.ADD_UTKAST_TO_INVENTERING ||
        mapMode === MapModeEnum.ADD_UTKAST_TO_SAMMANSATT ||
        mapMode === MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING) && (
        <InfoBox
          message="Klicka för att välja"
          buttonText="klar"
          onClick={() => {
            if (mapMode === MapModeEnum.ADD_UTKAST_TO_INVENTERING) {
              store.dispatch(setMapMode(MapModeEnum.INVENTERING_REGISTRATION));
              clearFeaturesInTemplateSources();
              store.dispatch(setDrawerOpen('open'));
            } else if (mapMode === MapModeEnum.ADD_UTKAST_TO_SAMMANSATT) {
              store.dispatch(setMapMode(MapModeEnum.SAMMANSATT_UTKAST_PRESENTATION));
            } else if (mapMode === MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING) {
              store.dispatch(setMapMode(MapModeEnum.INVENTERING_PRESENTATION));
            }
          }}
        />
      )}
      <div className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-w-full tw-justify-end tw-bg-[#fff9]/[0.6]">
        <div className="scale-line-container tw-relative tw-ml-4 tw-mr-4 tw-flex tw-justify-center tw-text-[0.813rem]">
          <div className="scale-line-border tw-absolute tw-h-[80%] tw-w-full tw-border-2 tw-border-t-0 tw-border-[#000000]/[0.6]" />
          <div id="scale-line" className="scale-line" />
        </div>
        <div className="tw-pr-[0.2rem] tw-text-[0.813rem]">
          <span className="tw-mr-4">{getCopyrightText(visibleBackgroundLayers)} N,E:</span>
          <span>
            {hoveredCoordinates[1].toString().split('.')[0]}, {hoveredCoordinates[0].toString().split('.')[0]}
          </span>
        </div>
      </div>
      {interactionOptions && <InteractionSelect />}
    </>
  );
};

export default MapComponent;
